/**
 * Partner portal routes (partners.quixomarketing.com) - owner: ui-agency-admin. Edit only inside this folder.
 * Public: /apply, /sign-in, /signin, /sign-in/link, /approve/:token (client approval, no login), /view-as (Super Admin view-as landing). Everything else sits behind
 * PartnerGate (approved partners only, B09-002).
 */
import type { RouteObject } from 'react-router';
import { AutoRagPage } from '../../features/auto-rag/AutoRagPage';
import { ApplyScreen, PartnerGate, PartnerRoot, SignInScreen } from './screens/layout';
import { ClientsPage, NewClientPage } from './screens/clients';
import { YouTubeReturnScreen } from './screens/youtube';
import { GooglePage, LocationsPage, MetaPage } from './screens/ads';
import { LinkedInAdsPage, SnapAdsPage, TikTokAdsPage } from './screens/ads-networks';
import { AdsOverviewPage } from './screens/ads-overview';
import { AccessPage, WhatsAppPage } from './screens/whatsapp';
import { ApprovalsPage, CalendarPage, DeliverablePage, NewDeliverablePage, ReportsPage } from './screens/delivery';
import { McpPage } from './screens/mcp';
import { BrandingPage } from './screens/brand';
import { PricingPage } from './screens/pricing';
import { SecurityScreen } from './screens/security';
import { HomePage } from './screens/home';
import { ClientPerformancePage } from './screens/performance';
import { ClientAnalyticsPage } from './screens/analytics';
import { ApprovalPage } from './screens/approval';
import { BacklogPage, ContentItemPage, ContentListPage, DailyGridPage, DashboardPage, MyWorkPage, NotificationsPage, ScopePage, SettingsPage } from './screens/content';
import { ContentCalendarPage } from './screens/calendar';
import { SocialPage } from './screens/social';
import { ViewAsScreen } from './screens/view-as';
import { AgencyOpportunityPage, AgencyQueuePage, AgencyStaffPage, PurchasePage, PurchasesPage } from './screens/opportunities';
import { CrmActivitiesPage, CrmCompaniesPage, CrmContactsPage, CrmImportPage, CrmLeadsPage, CrmPipelinePage, CrmTasksPage } from './screens/crm';

export const PARTNER_PATHS = [
  '', 'clients', 'clients/new', 'clients/:id', 'clients/:id/performance', 'clients/:id/analytics', 'ads/overview', 'ads/meta', 'ads/google', 'ads/tiktok', 'ads/snap', 'ads/linkedin', 'locations', 'whatsapp', 'calendar', 'deliverables/new',
  'deliverables/:id', 'reports', 'approvals', 'knowledge/ai-search', 'mcp', 'branding', 'access', 'pricing', 'security',
  'approve/:token', 'view-as', 'content', 'content/items/:id', 'content/calendar', 'content/my-work', 'content/backlog', 'content/scope', 'content/grid', 'content/dashboard', 'content/settings', 'content/notifications',
  'opportunities', 'opportunities/staff', 'opportunities/purchases', 'opportunities/purchases/:id', 'opportunities/:id',
  'social',
  'crm', 'crm/leads', 'crm/contacts', 'crm/companies', 'crm/tasks', 'crm/activities', 'crm/import',
  'integrations/connect',
] as const;

export const partnerRoutes: RouteObject[] = [
  {
    element: <PartnerRoot />,
    children: [
      { path: 'apply', element: <ApplyScreen /> },
      { path: 'sign-in', element: <SignInScreen /> },
      { path: 'signin', element: <SignInScreen /> },
      { path: 'sign-in/link', element: <SignInScreen /> },
      // outside the approval gate: reachable during forced 2FA enrolment (identity routes stay open then)
      { path: 'security', element: <SecurityScreen /> },
      // client approval (US-18): public, the token in the path is the only credential; never behind the approval gate
      { path: 'approve/:token', element: <ApprovalPage /> },
      // Super Admin view-as landing (#code=): public, it creates the session; never behind the approval gate
      { path: 'view-as', element: <ViewAsScreen /> },
      {
        element: <PartnerGate />,
        children: [
          { index: true, element: <HomePage /> },
          { path: 'clients', element: <ClientsPage /> },
          { path: 'clients/new', element: <NewClientPage /> },
          { path: 'clients/:id', element: <ClientsPage /> },
          { path: 'clients/:id/performance', element: <ClientPerformancePage /> },
          // the client's Google Analytics 4 (read-only; flag connectors.ga4 on the client, grant with read:reports)
          { path: 'clients/:id/analytics', element: <ClientAnalyticsPage /> },
          // YouTube OAuth return on the partners host (the worker's /bff/oauth/connectors/callback 303s here)
          { path: 'integrations/connect', element: <YouTubeReturnScreen /> },
          // all clients' ads in one table (flag connectors.agency_overview; listed only when its API answers)
          { path: 'ads/overview', element: <AdsOverviewPage /> },
          { path: 'ads/meta', element: <MetaPage /> },
          { path: 'ads/google', element: <GooglePage /> },
          // TikTok / Snap / LinkedIn (flag connectors.agency_networks): read-only status + per-client reports; the menu lists them only while the API answers
          { path: 'ads/tiktok', element: <TikTokAdsPage /> },
          { path: 'ads/snap', element: <SnapAdsPage /> },
          { path: 'ads/linkedin', element: <LinkedInAdsPage /> },
          { path: 'locations', element: <LocationsPage /> },
          { path: 'whatsapp', element: <WhatsAppPage /> },
          { path: 'calendar', element: <CalendarPage /> },
          { path: 'deliverables/new', element: <NewDeliverablePage /> },
          { path: 'deliverables/:id', element: <DeliverablePage /> },
          { path: 'reports', element: <ReportsPage /> },
          { path: 'approvals', element: <ApprovalsPage /> },
          { path: 'mcp', element: <McpPage /> },
          { path: 'branding', element: <BrandingPage /> },
          { path: 'knowledge/ai-search', element: <AutoRagPage /> },
          { path: 'access', element: <AccessPage /> },
          { path: 'pricing', element: <PricingPage /> },
          { path: 'content', element: <ContentListPage /> },
          { path: 'content/items/:id', element: <ContentItemPage /> },
          { path: 'content/calendar', element: <ContentCalendarPage /> },
          { path: 'content/my-work', element: <MyWorkPage /> },
          { path: 'content/backlog', element: <BacklogPage /> },
          { path: 'content/scope', element: <ScopePage /> },
          { path: 'content/grid', element: <DailyGridPage /> },
          { path: 'content/dashboard', element: <DashboardPage /> },
          { path: 'content/settings', element: <SettingsPage /> },
          { path: 'content/notifications', element: <NotificationsPage /> },
          // Opportunities (flag crm.opportunities): the agency's own sales desk, and buying from Quixo; the screens check the API answers
          { path: 'opportunities', element: <AgencyQueuePage /> },
          { path: 'opportunities/staff', element: <AgencyStaffPage /> },
          { path: 'opportunities/purchases', element: <PurchasesPage /> },
          { path: 'opportunities/purchases/:id', element: <PurchasePage /> },
          { path: 'opportunities/:id', element: <AgencyOpportunityPage /> },
          // agency social scheduling (flag social.agency_scheduling): the menu lists it only while GET /v1/social/agency/availability answers
          { path: 'social', element: <SocialPage /> },
          // agency CRM: the menu lists these only while GET /v1/crm/availability answers (flag crm.partner_crm)
          { path: 'crm', element: <CrmPipelinePage /> },
          { path: 'crm/leads', element: <CrmLeadsPage /> },
          { path: 'crm/contacts', element: <CrmContactsPage /> },
          { path: 'crm/companies', element: <CrmCompaniesPage /> },
          { path: 'crm/tasks', element: <CrmTasksPage /> },
          { path: 'crm/activities', element: <CrmActivitiesPage /> },
          { path: 'crm/import', element: <CrmImportPage /> },
        ],
      },
    ],
  },
];
